import React from "react";
import { Badge } from "@shared/ui/badge";
import { ValueLabel } from "@features/ax/shared/ValueLabel";

interface OverviewTabProps {
  resource: any;
}

export default function OverviewTab({ resource }: OverviewTabProps) {
  if (!resource) return null;

  return (
    <div className="space-y-6">
      {/* Resource Summary Section */}
      <div className="bg-white border border-gray-150 rounded-xl p-5 shadow-sm">
        <h4 className="text-sm font-semibold text-gray-800 mb-4">Resource Summary</h4>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          <ValueLabel label="Resource ID" value={resource.meterNumber || "NA"} />
          <ValueLabel label="Serial No" value={resource.deviceNo || "NA"} />
          <ValueLabel label="Utility service" value={resource.productCode || "NA"} />
          <ValueLabel label="Resource type" value={resource.resourceTypeDisplay || "NA"} />
          <div>
            <span className="block text-[10px] text-gray-400 font-medium mb-1">Assignment status</span>
            <Badge className="bg-green-50 text-green-700 border border-green-150 hover:bg-green-100 shadow-none font-semibold">
              {resource.status || resource.assignmentStatus || "NA"}
            </Badge>
          </div>
          <ValueLabel
            label="Last reading"
            value={resource.currentReading !== undefined && resource.currentReading !== null ? `${resource.currentReading} (${resource.currentReadingDate || "NA"})` : "NA"}
          />
        </div>
      </div>

      {/* Location Details Section */}
      <div className="bg-white border border-gray-150 rounded-xl p-5 shadow-sm">
        <h4 className="text-sm font-semibold text-gray-800 mb-4">Location Details</h4>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          <ValueLabel label="Area" value={resource.area || "NA"} />
          <ValueLabel label="Sub area" value={resource.subArea || "NA"} />
          <ValueLabel label="Premise" value={resource.premiseName || "NA"} />
          <ValueLabel label="Unit" value={resource.unitCodeDisplay || "NA"} />
          <ValueLabel label="Latitude" value={resource.lat || "NA"} />
          <ValueLabel label="Longitude" value={resource.long || "NA"} />
        </div>
      </div>

      {/* Unit Details Section */}
      <div className="bg-white border border-gray-150 rounded-xl p-5 shadow-sm">
        <h4 className="text-sm font-semibold text-gray-800 mb-4">Unit Details</h4>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          <ValueLabel label="Premise" value={resource.unitDetails?.premise || "NA"} />
          <ValueLabel label="Unit" value={resource.unitDetails?.unit || "NA"} />
          <div>
            <span className="block text-[10px] text-gray-400 font-medium mb-1">Status</span>
            <Badge className="bg-green-50 text-green-700 border border-green-150 shadow-none font-semibold">
              {resource.unitDetails?.status || "NA"}
            </Badge>
          </div>
          <ValueLabel label="Parcel id" value={resource.unitDetails?.parcelId || "NA"} />
          <div>
            <span className="block text-[10px] text-gray-400 font-medium mb-1">Occupancy status</span>
            <Badge className="bg-indigo-50 text-indigo-700 border border-indigo-150 shadow-none font-semibold">
              {resource.unitDetails?.occupancyStatus || "NA"}
            </Badge>
          </div>
          <ValueLabel label="Latitude" value={resource.unitDetails?.latitude || "NA"} />
          <ValueLabel label="Longitude" value={resource.unitDetails?.longitude || "NA"} />
          <div className="col-span-2">
            <span className="block text-[10px] text-gray-400 font-medium mb-1">Utility services</span>
            <div className="flex gap-1.5 flex-wrap">
              {resource.unitDetails?.utilityServices && resource.unitDetails.utilityServices.length > 0 ? (
                resource.unitDetails.utilityServices.map((service: string, idx: number) => {
                  let badgeColor = "bg-gray-50 text-gray-700 border-gray-150";
                  if (service.toLowerCase().includes("water")) badgeColor = "bg-cyan-50 text-cyan-700 border-cyan-155";
                  if (service.toLowerCase().includes("gas")) badgeColor = "bg-orange-50 text-orange-700 border-orange-150";
                  if (service.toLowerCase().includes("electric")) badgeColor = "bg-blue-50 text-blue-700 border-blue-150";
                  
                  return (
                    <Badge key={idx} className={`${badgeColor} border shadow-none font-semibold`}>
                      {service}
                    </Badge>
                  );
                })
              ) : (
                <span className="text-sm font-semibold text-gray-900">NA</span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Technical Specifications Section */}
      <div className="bg-white border border-gray-150 rounded-xl p-5 shadow-sm">
        <h4 className="text-sm font-semibold text-gray-800 mb-4">Technical Specifications</h4>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          <ValueLabel label="Resource Type" value={resource.meterModel?.resourceTypeDisplay} />
          <ValueLabel label="Resource Id" value={resource.meterNumber || "NA"} />
          <ValueLabel label="Serial no" value={resource.deviceNo || "NA"} />
          <ValueLabel label="Manufacturer" value={resource.manufacturerName || "NA"} />
          <ValueLabel label="Model" value={resource.modelName || "NA"} />
          <ValueLabel label="Material" value={resource.meterModel?.technicalSpecifications?.material || "NA"} />
          <ValueLabel label="Enclosure rating" value={resource.meterModel?.technicalSpecifications?.enclosureRating || "NA"} />
          <ValueLabel label="Installation date" value={resource.installedOn || resource.installationDate || "NA"} />
          <ValueLabel label="Mounting type" value={resource.meterModel?.technicalSpecifications?.mountingType || "NA"} />
          <ValueLabel label="Orientation" value={resource.meterModel?.technicalSpecifications?.installationOrientation || "NA"} />
          <ValueLabel label="Weight (kg)" value={resource.meterModel?.technicalSpecifications?.weight ?? "NA"} />
          <ValueLabel label="Environmental rating" value={resource.meterModel?.technicalSpecifications?.environmentalRating || "NA"} />
        </div>
      </div>
    </div>
  );
}
